import type { UnitBoardEntry, UnitPosition } from '../lib/types'
import { POSITION_ORDER, slotLabel } from '../lib/rules'

/**
 * The whole game in one picture: six position rooms down, 32 teams across.
 * Orange cell = spent, and the number in it is the week you spent it.
 *
 * Landscape, not portrait. Thirty-two rows of six cells was a tall thin ribbon
 * that wasted every pixel either side of it and needed scrolling to read at
 * all. Six rows of thirty-two fills the width a browser actually has, fits on
 * one screen without scrolling, and — the part that matters — is the shape
 * people screenshot into a group chat.
 *
 * The transpose also matches how the decision feels. You pick a position and
 * then scan the teams, so position is the stable axis and teams are the range
 * you sweep across.
 */
export default function BurnGrid({ board }: { board: UnitBoardEntry[] }) {
  const teams = [...new Set(board.map((b) => b.unit.team))].sort()
  const cell = (team: string, pos: UnitPosition) =>
    board.find((b) => b.unit.team === team && b.unit.position === pos)

  const burned = board.filter((b) => b.burned).length
  const total = board.length
  const pct = total ? Math.round((burned / total) * 100) : 0

  const spentIn = (p: UnitPosition) =>
    board.filter((b) => b.unit.position === p && b.burned).length

  return (
    <div className="burnboard">
      <header className="bb-head">
        <div className="bb-count">
          <span className="bb-n">{burned}</span>
          <span className="bb-d">
            of {total} rooms spent
            <em>{total - burned} still yours</em>
          </span>
        </div>
        <div className="bb-legend">
          <span><i className="sw sw-open" /> available</span>
          <span><i className="sw sw-burned" /> spent — number is the week</span>
        </div>
      </header>

      <div className="bb-meter" aria-hidden>
        <span style={{ width: `${pct}%` }} />
      </div>

      <div className="bb-scroll">
        <table className="bb-grid">
          <thead>
            <tr>
              <th className="bb-corner" />
              {teams.map((t) => (
                <th key={t} className="bb-teamhead">{t}</th>
              ))}
              <th className="bb-rowtotal">spent</th>
            </tr>
          </thead>
          <tbody>
            {POSITION_ORDER.map((p) => (
              <tr key={p}>
                <th className="bb-pos">{slotLabel(p)}</th>
                {teams.map((t) => {
                  const e = cell(t, p)
                  return (
                    <td
                      key={t}
                      className={e?.burned ? 'bb-cell is-burned' : 'bb-cell'}
                      title={
                        e?.burned
                          ? `${t} ${slotLabel(p)} — spent in Week ${e.burnedWeekNo}`
                          : `${t} ${slotLabel(p)} — still available`
                      }
                    >
                      {e?.burned ? e.burnedWeekNo : ''}
                    </td>
                  )
                })}
                <td className="bb-rowtotal">{spentIn(p)}<span>/32</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
